import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/*
 * Pinning the page belongs to the page, not to any one dialog.
 *
 * Dialogs stack: choosing a variant opens the quick add modal, and adding from
 * it opens the cart drawer on top. If each dialog remembered the page styles
 * for itself, the second one would record the styles the first had already
 * changed, and closing it would put `position: fixed` back on a page with no
 * dialog open. The shopper is then stuck until they reload.
 *
 * So the lock is held once for the whole page and counted. The first dialog to
 * ask for it records what the page really looked like; the last one to let go
 * puts that back and returns the shopper to where they were.
 */
let bodyLockCount = 0;

/** @type {{width: string, position: string, top: string, left: string, right: string, paddingRight: string} | null} */
let bodyLockStyles = null;

let bodyLockScrollY = 0;

/**
 * Pins the page behind a dialog.
 * @returns {number} The scroll position to return to when the last lock lifts.
 */
function lockBodyScroll() {
  if (bodyLockCount === 0) {
    bodyLockScrollY = window.scrollY;
    bodyLockStyles = {
      width: document.body.style.width,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      paddingRight: document.body.style.paddingRight,
    };

    const bodyRect = document.body.getBoundingClientRect();

    document.body.style.width = `${bodyRect.width}px`;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyLockScrollY}px`;
    document.body.style.left = `${bodyRect.left}px`;
    document.body.style.right = '0';
  }

  bodyLockCount += 1;

  return bodyLockScrollY;
}

/**
 * Releases one hold on the page, and restores it once none are left.
 */
function releaseBodyScroll() {
  if (bodyLockCount === 0) return;

  bodyLockCount -= 1;
  if (bodyLockCount > 0) return;

  if (bodyLockStyles) {
    document.body.style.width = bodyLockStyles.width;
    document.body.style.position = bodyLockStyles.position;
    document.body.style.top = bodyLockStyles.top;
    document.body.style.left = bodyLockStyles.left;
    document.body.style.right = bodyLockStyles.right;
    document.body.style.paddingRight = bodyLockStyles.paddingRight;
    bodyLockStyles = null;
  }

  window.scrollTo({ top: bodyLockScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** Whether this dialog is currently one of the holders of the page lock. */
  #holdsBodyLock = false;

  /**
   * True between showDialog() and the frame that actually opens the dialog.
   *
   * The open is deferred to an animation frame, so `dialog.open` is still false
   * for that frame. Without this flag a second showDialog() in the same tick,
   * which happens whenever two cart events land together or a shopper double
   * taps add to cart, passes the guard as well, and takes a second hold on the
   * page that nothing will ever release.
   */
  #isOpening = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#isOpening) return;
    this.#isOpening = true;

    const isNonModalSearch = this.id === 'search-modal';

    requestAnimationFrame(() => {
      this.#isOpening = false;

      if (!isNonModalSearch && !this.#holdsBodyLock) {
        lockBodyScroll();
        this.#holdsBodyLock = true;
      }

      if (isNonModalSearch) {
        dialog.show();
      } else {
        dialog.showModal();
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Lets go of this dialog's hold on the page, if it has one.
   */
  #releaseBody() {
    if (!this.#holdsBodyLock) return;

    this.#holdsBodyLock = false;
    releaseBodyScroll();
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // A dialog that is already shut can still be holding the page, if it was
    // re-rendered or closed by something other than this method. Let go before
    // leaving, so a close can never strand the shopper.
    if (!dialog.open) {
      this.#releaseBody();
      return;
    }
    const isNonModalSearch = this.id === 'search-modal';

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // The scroll position comes back with the last hold, inside releaseBodyScroll
    this.#releaseBody();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
